import React from 'react'
import { useNavigate } from 'react-router-dom'

import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import BasicButton, {
  BASIC_BUTTON_COLOR_ACTION,
  BASIC_BUTTON_SIZE_SMALL,
  BASIC_BUTTON_TYPE_OUTLINED,
} from '@divvy-web/skylab.basicbutton'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'

import { useAmendSentToSignerApp } from './AmendSentToSignerApp.gql'

interface AwaitingSignatureBannerProps {
  salesforceCreditId: string
}

/* This status shows if status is awaiting_signature and is not the authorized signer and they are waiting for the auth signer to review and submit the application
 */
const AwaitingSignatureBanner = ({ salesforceCreditId }: AwaitingSignatureBannerProps) => {
  const { isAppVersion3, isLoadingCreditAppTypes } = useGetCreditAppTypes({
    salesforceCreditId: salesforceCreditId || '',
  })
  const navigate = useNavigate()
  const showToast = useToast()

  //TODO: make new query and call refetch getapplicationdetails
  const [amendSentToSignerApp, { loading: isLoadingAmendSentToSigner }] = useAmendSentToSignerApp({
    variables: { salesforceCreditId },
    refetchQueries: ['GetCreditApplicationDetails'],
    onCompleted: () => {
      logInfo('amendApplication', {
        attributes: {
          creditAppId: salesforceCreditId,
          result: 'Application amended',
        },
        eventName: 'amendSentToSignerApp',
      })

      isAppVersion3
        ? navigate(`/app/${salesforceCreditId}/${PATHNAME_CONSTS.REVIEW_PATH}`)
        : navigate(`/app/${salesforceCreditId}/section/${PATHNAME_CONSTS.REVIEW_AND_SIGN_PATH}`)
    },
    onError: (error) => {
      logError(error, {
        attributes: {
          action: 'amendApplication',
          creditAppId: salesforceCreditId,
          result: 'Application amend failed',
          message: error?.message,
        },
        eventName: 'amendSentToSignerApp',
      })

      showToast(
        TOAST_TYPE_DANGER,
        <FormattedMessage
          defaultMessage='Error amending credit application.'
          id='sputnik.AwaitingSignatureStatusTile__kBwmHX'
        />,
        { className: 'fs-unmask' },
      )
    },
  })

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Informative}
        subtext={
          <>
            <FormattedMessage
              defaultMessage='We’re waiting for the authorized signer to review and sign the application.'
              id='sputnik.AwaitingSignatureBanner__dezVS5'
            />
            <BasicButton
              color={BASIC_BUTTON_COLOR_ACTION}
              disabled={isLoadingCreditAppTypes || isLoadingAmendSentToSigner}
              size={BASIC_BUTTON_SIZE_SMALL}
              type={BASIC_BUTTON_TYPE_OUTLINED}
              onClick={() => amendSentToSignerApp()}
            >
              <FormattedMessage
                defaultMessage='Amend application'
                id='sputnik.AwaitingSignatureBanner__BB1eP1'
              />
            </BasicButton>
          </>
        }
        title={
          <FormattedMessage
            defaultMessage='Awaiting signature'
            id='sputnik.AwaitingSignatureBanner__1JRfgv'
          />
        }
      />
    </div>
  )
}

export default AwaitingSignatureBanner
